import { useState, useRef } from 'react';
import { type QueryLazyOptions, useLazyQuery } from '@apollo/react-hooks';
import type { ApolloError } from 'apollo-client';
import { fg } from '@atlassian/jira-feature-gating';
import { handleApolloError } from '@atlassian/jira-issue-create-commons/src/common/utils/handle-apollo-error/index.tsx';
import { useIssueCreateErrorAnalyticsEvent } from '../../../issue-create-analytics/src/controllers/use-error-analytics-events/index.tsx';
import type { QueryVariables, FetchConfigData } from '../../common/types/index.tsx';
import {
	transformQueryData,
	transformQueryResults,
} from '../../common/utils/fetch-config-query/index.tsx';
import { useSmartFieldsMachineLearning } from '../../common/utils/use-smart-fields-ml/index.tsx';
import { getCreateConfigQuery } from '../common/gql.tsx';
import { useModalLoadUiTaskEvents } from '../../../issue-create-analytics/src/controllers/use-modal-load-ui-task-events/index.tsx';
import type { CreateConfigQuery } from '../../common/types/create-config-query.tsx';
import { getCreateConfigUserPreferredQuery } from './gql.tsx';
import type { UseFetchCreateConfigQuery } from './types.tsx';

const useFetchCreateConfigQuery: UseFetchCreateConfigQuery = (props = {}) => {
	const { fireErrorAnalyticsEvent } = useIssueCreateErrorAnalyticsEvent();
	const { fireModalLoadTaskFailEvent, fireModalLoadTaskAbortEvent } = useModalLoadUiTaskEvents();
	const { displayOnlyRequiredFields = false } = props;
	const [loading, setLoading] = useState(false);
	const [fetchError, setFetchError] = useState<ApolloError | null>(null);
	const apiStartTime = useRef<number>(0);
	const [apiLatency, setApiLatency] = useState<number>(0);
	const [data, setData] = useState<FetchConfigData>({
		fieldTabs: [],
		fieldConfig: {},
		contextFields: {},
		meta: {
			areRequiredFieldsSupported: true,
			queryErrorMessage: '',
		},
		additionalServices: {},
	});

	const { withMachineLearning } = useSmartFieldsMachineLearning();

	const createConfigQuery = fg('new-issue-create-fetch-config-query')
		? getCreateConfigQuery({
				isInContext: false,
				withML: withMachineLearning,
				isJsmRespondersFieldEnabled: fg('jsm-responders-field-in-gicx-ui'),
			})
		: getCreateConfigUserPreferredQuery(withMachineLearning);

	const [fetchConfig] = useLazyQuery<CreateConfigQuery, QueryVariables>(createConfigQuery, {
		fetchPolicy: 'no-cache',
		onCompleted: (queryData) => {
			setApiLatency(Math.floor(performance.now() - apiStartTime.current));
			setData(
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				transformQueryResults(
					transformQueryData(queryData),
					displayOnlyRequiredFields,
				) as FetchConfigData,
			);
			setLoading(false);
		},
		onError: (error: ApolloError) => {
			setFetchError(error);
			const errorType = handleApolloError(error);
			fireErrorAnalyticsEvent({
				meta: {
					id: 'useFetchCreateConfigQuery',
					packageName: 'jiraIssueCreate',
					teamName: 'gryffindor',
				},
				error,
				// TODO: remove this from here and ensure this is moving in attibutes
				errorType,
			});
			if (errorType === 'graphQLError') {
				fireModalLoadTaskFailEvent();
			} else {
				fireModalLoadTaskAbortEvent();
			}
		},
	});
	const handleFetch = useRef((queryVariables?: QueryLazyOptions<QueryVariables>) => {
		setLoading(true);
		apiStartTime.current = performance.now();
		fetchConfig(queryVariables);
	});
	return [
		{
			isLoading: loading,
			fieldTabs: data.fieldTabs,
			fieldConfig: data.fieldConfig,
			contextFields: data.contextFields,
			meta: data.meta,
			error: fetchError,
			apiLatency,
			additionalServices: data.additionalServices,
		},
		handleFetch.current,
	];
};
export default useFetchCreateConfigQuery;
